/**
 * The top of the account page: a link back to the dashboard, the account's
 * name, and small print saying whether it's a house account, when it last
 * ordered, and how long it's been a customer.
 */

import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { SALES_INSIGHTS_ROUTE } from "@/hooks/use-sales-insights";
import type { SalesInsightsAccountResponse } from "@onsemble/shared/types/sales-insights";
import { date } from "../../format";
import { lastOrderText } from "../../shared/lastOrderText";

export function Header({ account }: { account: SalesInsightsAccountResponse }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3">
      {/* The account's name on the left, the way back to the dashboard at the far right */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <h1 className="m-0 text-si-2xl font-semibold leading-[1.15] tracking-[-0.02em] max-sm:text-si-xl">
          {account.name}
        </h1>
        {/* An outlined pill button, the prototype's secondary-button style */}
        <Link
          href={SALES_INSIGHTS_ROUTE}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-si-line-strong bg-si-bg px-4 text-si-sm font-semibold text-si-ink transition-colors hover:border-si-ink hover:bg-si-surface"
        >
          ← {t("pages.pcInsights.list.yourAccounts")}
        </Link>
      </div>
      {/* Small print: house account (if it is one) · last order · customer since */}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-si-xs text-si-ink-3">
        {account.assignment === "house" &&<span>{t("pages.pcInsights.account.houseAccount")}</span>}
        <span>{lastOrderText(t, account.lastOrder?.daysAgo ?? null, "account")}</span>
        {/* Only when Eclipse has a first order date */}
        {account.firstOrderDate && (
          <span>
            {t("pages.pcInsights.account.customerSince", { date: date(account.firstOrderDate) })}
          </span>
        )}
      </div>
    </div>
  );
}
